import { type BoardState, type Point, type Stroke } from './board'
import { whiteboardDimensions as courtDimensions, whiteboardViewport } from './whiteboardLayout'
import { along } from './play'
import { themeColors } from './theme'

export type AnimateItem = { kind: 'player'; id: string } | { kind: 'ball' }
export type AnimateAction = { kind: 'cut' | 'dribble' | 'screen' | 'pass'; playerId: string; points: Point[]; targetId?: string }
export type AnimateDrag = { item: AnimateItem; points: Point[] }
type TimedAction = AnimateAction & { duration: number }
export type AnimationGroup = { start: BoardState; end: BoardState; movements: TimedAction[]; pass?: TimedAction; duration: number }
export type AnimationView = {
  frame?: BoardState; busy: boolean; playing: boolean; status: string; previews: Stroke[]
  destinations: Point[]; targets: string[]; eligible: string[]; dribbling: string[]
}
export const emptyAnimationView = (): AnimationView => ({ busy: false, playing: false, status: '', previews: [], destinations: [], targets: [], eligible: [], dribbling: [] })

export function svgDistance(a: Point, b: Point, board: BoardState): number {
  const { width, height } = courtDimensions(!!board.fullCourt)
  return Math.hypot((a.x-b.x)*width, (a.y-b.y)*height)
}
export function svgLength(points: Point[], board: BoardState): number {
  return points.slice(1).reduce((sum, p, i) => sum + svgDistance(points[i], p, board), 0)
}
function interpolate(points: Point[], fraction: number, board: BoardState): Point {
  const { width, height } = courtDimensions(!!board.fullCourt)
  const p = along(points.map(p => ({ x: p.x*width, y: p.y*height })), fraction)
  return { x: p.x/width, y: p.y/height }
}
function ballPoint(point: Point, board: BoardState): Point {
  const { width, height } = courtDimensions(!!board.fullCourt)
  const direction = board.flipped ? -1 : 1
  return { x: point.x+direction*20/width, y: point.y-direction*14/height }
}

// Shift the start to the projected origin, fading the shift along the route so
// the destination stays where the coach dropped it.
export function rebaseRoute(points: Point[], origin: Point, board: BoardState): Point[] {
  const total = svgLength(points, board)
  let traversed = 0
  return points.map((p, i) => {
    if (i) traversed += svgDistance(points[i-1], p, board)
    const weight = total ? 1-traversed/total : 1
    return { x: p.x+(origin.x-points[0].x)*weight, y: p.y+(origin.y-points[0].y)*weight }
  })
}

export function inferAction(board: BoardState, drag: AnimateDrag, hitBoard = board): AnimateAction | undefined {
  if (drag.points.length < 2 || svgLength(drag.points, board) < 5) return
  const id = drag.item.kind === 'ball' ? board.ballHandlerId : drag.item.id
  const player = board.players.find(p => p.id === id)
  if (!player) return
  const isBall = drag.item.kind === 'ball'
  const points = rebaseRoute(drag.points, isBall ? ballPoint(player, board) : player, board)
  const drop = points.at(-1)!
  const hit = hitBoard.players.filter(p => (isBall || p.id !== id) && (p.side === 'offense' || hitBoard.showDefense))
    .map(p => ({ player: p, distance: svgDistance(p, drop, board) }))
    .filter(p => p.distance <= 60).sort((a,b) => a.distance-b.distance)[0]?.player
  const target = board.players.find(p => p.id === hit?.id)
  if (isBall) {
    if (!target || target.id === id || target.side !== player.side) return
    points[points.length-1] = ballPoint(target, board)
    return { kind: 'pass', playerId: player.id, targetId: target.id, points }
  }
  if (id === board.ballHandlerId) return { kind: 'dribble', playerId: player.id, points }
  if (!target) return { kind: 'cut', playerId: player.id, points }
  const { width, height } = courtDimensions(!!board.fullCourt)
  let approach = points.length-2
  while (approach > 0 && svgDistance(points[approach],target,board) < 65) approach--
  const previous = points[approach] || player
  const angle = Math.atan2((previous.y-target.y)*height, (previous.x-target.x)*width)
  // Near the apron boundary, choose the closest approach that leaves both
  // markers inside the drawable area and preserves their separation.
  const bounds = whiteboardViewport(board)
  const candidates = [angle, ...Array.from({ length: 16 }, (_,i) => i*Math.PI/8)]
    .map(a => ({ x: target.x+Math.cos(a)*65/width, y: target.y+Math.sin(a)*65/height }))
    .filter(p => p.x*width >= bounds.x+30 && p.x*width <= bounds.x+bounds.width-30 &&
      p.y*height >= bounds.y+30 && p.y*height <= bounds.y+bounds.height-30)
  points.splice(approach+1)
  points.push(candidates[0] || player)
  return { kind: 'screen', playerId: player.id, targetId: target.id, points }
}

export function scheduleAnimation(board: BoardState, actions: AnimateAction[]): AnimationGroup {
  const movements: TimedAction[] = actions.filter(a => a.kind !== 'pass').map(action => ({
    ...action, points: rebaseRoute(action.points, board.players.find(p => p.id === action.playerId)!, board),
    duration: Math.max(150, svgLength(action.points,board)/182*1000),
  }))
  const players = board.players.map(p => ({ ...p, ...(movements.find(a => a.playerId === p.id)?.points.at(-1) || p) }))
  const request = actions.find(a => a.kind === 'pass' && a.playerId === board.ballHandlerId)
  const receiver = players.find(p => p.id === request?.targetId)
  let pass: TimedAction | undefined
  if (request && receiver && receiver.side === board.players.find(p => p.id === request.playerId)?.side) {
    const points = [...request.points]
    points[points.length-1] = ballPoint(receiver,board)
    pass = { ...request, points, duration: Math.max(350, svgLength(points,board)/600*1000, movements.find(a => a.playerId === receiver.id)?.duration || 0) }
  }
  const end = { ...board, players, ballHandlerId: pass?.targetId || board.ballHandlerId, looseBall: undefined, updatedAt: Date.now() }
  return { start: board, end, movements, pass, duration: Math.max(0,...movements.map(a => a.duration),pass?.duration || 0) }
}

export function animationFrame(group: AnimationGroup, elapsed: number): BoardState {
  const players = group.start.players.map(p => {
    const action = group.movements.find(a => a.playerId === p.id)
    return action ? { ...p, ...interpolate(action.points,elapsed/action.duration,group.start) } : p
  })
  const inFlight = group.pass && elapsed < group.pass.duration
  return { ...group.start, players, ballHandlerId: inFlight ? null : group.end.ballHandlerId,
    looseBall: inFlight ? interpolate(group.pass!.points,elapsed/group.pass!.duration,group.start) : undefined }
}

export class WhiteboardAnimator {
  private base: BoardState
  private enabled = false
  private pending: AnimateAction[] = []
  private drag: AnimateDrag | null = null
  private playing: { group: AnimationGroup; started: number } | null = null
  private deadline = 0
  private timer?: ReturnType<typeof setTimeout>
  private raf?: number
  private hiddenAt: number | null = null
  private message = ''
  constructor(board: BoardState, private onView: (view: AnimationView) => void, private onCommit: (board: BoardState) => void) { this.base = board }

  sync(board: BoardState, enabled: boolean) {
    if (board !== this.base || enabled !== this.enabled) {
      this.cancel(); this.base = board; this.enabled = enabled
    }
  }
  private projected() { return this.playing?.group.end || this.base }
  private inferDrag() {
    if (!this.drag) return
    const visible = this.playing ? animationFrame(this.playing.group,performance.now()-this.playing.started) : this.base
    return inferAction(this.projected(),this.drag,visible)
  }
  begin(item: AnimateItem, point: Point) {
    if (!this.enabled || this.hiddenAt !== null) return
    if (item.kind === 'ball' && !this.projected().ballHandlerId) return
    clearTimeout(this.timer)
    this.message = ''
    this.drag = { item, points: [point] }
    this.emit()
  }
  move(point: Point) {
    if (!this.drag) return
    const points = this.drag.points
    if (svgDistance(points.at(-1)!,point,this.base) >= 2) {
      points.push(point)
      if (points.length > 1024) this.drag.points = points.filter((_,i) => i%2 === 0 || i === points.length-1)
    }
    this.emit()
  }
  drop(point?: Point, canceled = false) {
    if (!this.drag) return
    if (point && !canceled) this.drag.points.push(point)
    const action = canceled ? undefined : this.inferDrag()
    if (action) {
      this.pending = this.pending.filter(a => action.kind === 'pass' ? a.kind !== 'pass' : a.kind === 'pass' || a.playerId !== action.playerId)
      this.pending.push(action)
    } else if (!canceled && this.drag.item.kind === 'ball') this.message = 'Pass canceled. Drop the ball on a teammate.'
    this.drag = null
    this.deadline = performance.now()+500
    this.emit(); this.arm()
  }
  cancel = () => {
    clearTimeout(this.timer)
    if (this.raf !== undefined) cancelAnimationFrame(this.raf)
    this.raf = undefined; this.timer = undefined
    this.pending = []; this.playing = null; this.drag = null; this.message = ''
    this.emit()
  }
  dispose() { this.cancel() }
  setHidden(hidden: boolean) {
    if (hidden && this.hiddenAt === null) {
      this.hiddenAt = performance.now()
      clearTimeout(this.timer)
      if (this.raf !== undefined) cancelAnimationFrame(this.raf)
    } else if (!hidden && this.hiddenAt !== null) {
      const delay = performance.now()-this.hiddenAt
      if (this.playing) this.playing.started += delay
      this.deadline += delay; this.hiddenAt = null
      if (this.playing) this.raf = requestAnimationFrame(this.tick)
      this.arm()
    }
  }
  private arm() {
    clearTimeout(this.timer)
    if (!this.enabled || this.hiddenAt !== null || this.playing || this.drag || !this.pending.length) return
    this.timer = setTimeout(() => {
      const group = scheduleAnimation(this.base,this.pending)
      this.pending = []; this.playing = { group, started: performance.now() }
      this.emit(); this.raf = requestAnimationFrame(this.tick)
    }, Math.max(0,this.deadline-performance.now()))
  }
  private tick = () => {
    if (!this.playing || this.hiddenAt !== null) return
    if (performance.now()-this.playing.started >= this.playing.group.duration) {
      this.base = this.playing.group.end
      this.playing = null
      this.onCommit(this.base)
      this.emit(); this.arm()
    } else { this.emit(); this.raf = requestAnimationFrame(this.tick) }
  }
  private emit() {
    const view = emptyAnimationView()
    const projected = this.projected()
    const dragging = this.inferDrag()
    const actions: AnimateAction[] = [...this.pending]
    if (dragging) {
      const index = actions.findIndex(a => dragging.kind === 'pass' ? a.kind === 'pass' : a.kind !== 'pass' && a.playerId === dragging.playerId)
      if (index >= 0) actions.splice(index,1)
      actions.push(dragging)
    } else if (this.drag && this.drag.points.length > 1) {
      const id = this.drag.item.kind === 'player' ? this.drag.item.id : projected.ballHandlerId
      const origin = projected.players.find(p => p.id === id)
      if (origin) actions.push({ kind: this.drag.item.kind === 'ball' ? 'pass' : 'cut', playerId: origin.id,
        points: rebaseRoute(this.drag.points,this.drag.item.kind === 'ball' ? ballPoint(origin,projected) : origin,projected) })
    }
    // Recompute pass destinations whenever its receiver's staged route changes.
    const preview = scheduleAnimation(projected,actions)
    const previews: AnimateAction[] = [...preview.movements,...(preview.pass ? [preview.pass] : [])]
    // Keep an invalid ball drag visible, but never stage it as an action.
    if (this.drag?.item.kind === 'ball' && !dragging) {
      const invalid = actions.find(a => a.kind === 'pass' && !a.targetId)
      if (invalid) previews.push(invalid)
    }
    const active = this.playing
    const elapsed = active ? performance.now()-active.started : 0
    if (active) {
      view.frame = animationFrame(active.group,elapsed)
      view.dribbling = active.group.movements.filter(a => a.playerId === view.frame!.ballHandlerId && elapsed < a.duration).map(a => a.playerId)
    }
    const running: AnimateAction[] = active ? [...active.group.movements.map(a => ({ ...a, kind: a.kind === 'dribble' && view.frame?.ballHandlerId !== a.playerId ? 'cut' as const : a.kind })),...(active.group.pass ? [active.group.pass] : [])] : []
    view.previews = [...running,...previews].map((a,i) => ({ id: `animate-${i}`, kind: 'arrow', points: a.points, style: a.kind,
      color: i < running.length ? themeColors.blaze : themeColors.electricBlueDark }))
    view.destinations = previews.map(a => a.points.at(-1)!)
    view.targets = previews.flatMap(a => a.targetId ? [a.targetId] : [])
    if (this.drag?.item.kind === 'ball') {
      const holder = projected.players.find(p => p.id === projected.ballHandlerId)
      view.eligible = projected.players.filter(p => p.id !== holder?.id && p.side === holder?.side && (p.side === 'offense' || projected.showDefense)).map(p => p.id)
    }
    view.playing = !!active
    view.busy = !!(active || this.pending.length || this.drag)
    view.status = active ? this.pending.length || this.drag ? 'Playing · Next group queued' : 'Playing' : this.pending.length || this.drag ? 'Preparing actions' : this.message
    this.onView(view)
  }
}
